/**
 * SSE over fetch — the console's side of `GET /integration/v1/events/stream` (N9 slice 2).
 *
 * The browser's `EventSource` would be the obvious consumer, and it is exactly wrong here:
 * it cannot send an `Authorization` header, and the console's only credential is the
 * short-lived session JWT (plan D1 of U-08 — no proxy, no BFF, no cookie). So the stream is
 * consumed through `fetch` with the bearer on it and a `ReadableStream` reader on the body.
 *
 * Two layers, kept apart because they fail differently:
 *  - `SseParser` is a pure framing state machine (the WHAT of a chunk: frames or not yet).
 *  - `openEventStream` is the connection loop (the WHEN: reconnect, backoff, stop).
 *
 * The server half of this contract lives in `acs_service/routers/events_route.py`: every
 * event block carries `id: <opaque cursor token>`; the body ends after a bounded lifetime
 * with `event: reconnect`; quiet stretches carry `: ping` comments. A reconnect therefore
 * resumes from the last seen id and replays nothing.
 */

export type SseStreamStatus = 'connecting' | 'live' | 'reconnecting' | 'stopped';

/** One dispatched frame. `id`/`event` are absent when the block named none. */
export interface SseFrame {
  id?: string;
  event?: string;
  data: string;
}

/**
 * The framing state machine (WHATWG server-sent-events, the subset the door speaks).
 *
 * Fed incrementally — a chunk boundary can land mid-line, so `push` keeps the trailing
 * partial line and `flush` dispatches a frame the body ended without its blank line.
 * Field rules that matter here: `:` opens a comment (the heartbeat — not an event);
 * repeated `data:` lines join with `\n`; `id` sets the last-event-id buffer that PERSISTS
 * across frames (spec behaviour — the door sends it on every block anyway); `event` is
 * per-frame and resets on dispatch.
 */
export class SseParser {
  private buffer = '';
  private dataLines: string[] = [];
  private pendingId: string | undefined;
  private pendingEvent: string | undefined;

  push(chunk: string): SseFrame[] {
    this.buffer += chunk;
    const frames: SseFrame[] = [];
    let index = this.buffer.indexOf('\n');
    while (index !== -1) {
      const line = this.buffer.slice(0, index).replace(/\r$/, '');
      this.buffer = this.buffer.slice(index + 1);
      const frame = this.consumeLine(line);
      if (frame !== null) frames.push(frame);
      index = this.buffer.indexOf('\n');
    }
    return frames;
  }

  /** The body ended: a half-written frame is still a frame. */
  flush(): SseFrame[] {
    const frame = this.dispatch();
    return frame === null ? [] : [frame];
  }

  private consumeLine(line: string): SseFrame | null {
    if (line === '') return this.dispatch();
    if (line.startsWith(':')) return null; // comment — `: ping` is liveness, not content
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'data') this.dataLines.push(value);
    else if (field === 'id') this.pendingId = value === '' ? undefined : value;
    else if (field === 'event') this.pendingEvent = value;
    return null;
  }

  private dispatch(): SseFrame | null {
    if (this.dataLines.length === 0) return null;
    const frame: SseFrame = {
      data: this.dataLines.join('\n'),
      ...(this.pendingId === undefined ? {} : { id: this.pendingId }),
      ...(this.pendingEvent === undefined ? {} : { event: this.pendingEvent }),
    };
    this.dataLines = [];
    this.pendingEvent = undefined;
    return frame;
  }
}

export interface OpenEventStreamOptions {
  url: string;
  /** Supplies the bearer per attempt — a fresh session token, not a cached corpse. */
  token: () => Promise<string>;
  onEvent: (frame: SseFrame) => void;
  onStatus: (status: SseStreamStatus) => void;
  signal: AbortSignal;
  /** Fired ONCE when a door answered 401 to the bearer — the session is gone. */
  onUnauthorized?: (status: number) => void;
}

export interface EventStreamHandle {
  close(): void;
}

/**
 * Backoff ladder for FAILED attempts — deterministic on purpose (plan D8): the only
 * consumer is one browser per operator, so jitter would buy nothing and cost a
 * reproducible test. The door's own `event: reconnect` end-of-life reconnects
 * immediately, outside this ladder.
 */
export const SSE_BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 15_000, 30_000] as const;

/**
 * The connection loop. First attempt reports `connecting`; a successful body reports
 * `live`; a failed attempt waits out the ladder under `reconnecting`; a normal body end
 * (with or without the `event: reconnect` hint) redials at once with `Last-Event-ID`.
 * A 401 fires `onUnauthorized` once and stops — redialing a dead session is noise.
 */
export function openEventStream(options: OpenEventStreamOptions): EventStreamHandle {
  const controller = new AbortController();
  let closed = false;
  let lastId: string | undefined;
  let stoppedByUs = false;

  const forwardExternalAbort = () => finish();
  if (options.signal.aborted) finish();
  else options.signal.addEventListener('abort', forwardExternalAbort, { once: true });

  function finish(): void {
    if (closed) return;
    closed = true;
    stoppedByUs = true;
    controller.abort();
    options.signal.removeEventListener('abort', forwardExternalAbort);
    options.onStatus('stopped');
  }

  function run(): void {
    void (async () => {
      let attempt = 0;
      let everLive = false;
      while (!closed) {
        const firstAttempt = attempt === 0;
        options.onStatus(firstAttempt || everLive ? 'connecting' : 'reconnecting');
        const attemptController = new AbortController();
        const abortIfClosed = () => attemptController.abort();
        if (closed) break;
        options.signal.addEventListener('abort', abortIfClosed, { once: true });
        controller.signal.addEventListener('abort', abortIfClosed, { once: true });
        try {
          const bearer = await options.token();
          const headers: Record<string, string> = {
            Accept: 'text/event-stream',
            Authorization: `Bearer ${bearer}`,
            ...(lastId === undefined ? {} : { 'Last-Event-ID': lastId }),
          };
          const response = await fetch(options.url, {
            headers,
            signal: attemptController.signal,
          });
          if (!response.ok) {
            if (response.status === 401) {
              options.onUnauthorized?.(401);
              finish();
              return;
            }
            throw new Error(`stream answered ${response.status}`);
          }
          everLive = true;
          attempt = 0;
          options.onStatus('live');
          const reader = response.body?.getReader();
          if (reader === undefined) throw new Error('stream body is not readable');
          const parser = new SseParser();
          const decoder = new TextDecoder();
          for (;;) {
            const { done, value } = await reader.read();
            if (done || closed) break;
            for (const frame of parser.push(decoder.decode(value, { stream: true }))) {
              if (frame.id !== undefined) lastId = frame.id;
              if (!closed) options.onEvent(frame);
            }
          }
          for (const frame of parser.push(decoder.decode())) {
            if (frame.id !== undefined) lastId = frame.id;
            if (!closed) options.onEvent(frame);
          }
          for (const frame of parser.flush()) {
            if (frame.id !== undefined) lastId = frame.id;
            if (!closed) options.onEvent(frame);
          }
          // Normal body end (the door's max-duration `event: reconnect` or a plain
          // close): redial AT ONCE, resuming from the last id — no backoff.
        } catch {
          if (stoppedByUs) return;
          attempt = Math.min(attempt + 1, SSE_BACKOFF_MS.length);
          options.onStatus('reconnecting');
          await new Promise((resolve) => setTimeout(resolve, SSE_BACKOFF_MS[attempt - 1]));
        } finally {
          options.signal.removeEventListener('abort', abortIfClosed);
          controller.signal.removeEventListener('abort', abortIfClosed);
        }
      }
    })();
  }

  run();
  return { close: finish };
}
